import React, { useEffect, useState, useRef } from 'react';
import { MessageCircleIcon, UserIcon, ChevronLeftIcon, ChevronRightIcon, HeartIcon, MessageSquareIcon, UsersIcon, ZapIcon, AppWindowIcon, Clock3Icon, GlobeIcon, LayersIcon, PuzzleIcon } from 'lucide-react';
import { SingleDivider } from './SingleDivider';
const HowItWorksSection = () => {
  // State to track which sections are visible - separate states for text and images
  const [visibleText, setVisibleText] = useState({
    section1: false,
    section2: false
  });
  const [visibleImages, setVisibleImages] = useState({
    section1: false,
    section2: false
  });
  // Carousel state for feature carousel
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = 3;
  // App logos data with user-provided images for messaging apps, social media, and dating apps
  const appCategories = [{
    name: 'Messaging Apps',
    icon: MessageSquareIcon,
    color: 'text-blue-500',
    iconColor: 'text-blue-500',
    gradient: 'from-blue-400 to-blue-600',
    apps: [{
      name: 'iMessage',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ps5AtGbt1BGqkwnS1t7JnL/iMessage.png'
    }, {
      name: 'Messenger',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7SDegHWn98u9m9SoRiDQPt/Messenger.png'
    }, {
      name: 'WhatsApp',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9sLnvddzLKwqS1LtTiwSBt/Whatsapp.png'
    }, {
      name: 'Telegram',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7u9T93q5NcXDg96dyVUKyf/Telegram.png'
    }]
  }, {
    name: 'Social Media',
    icon: UsersIcon,
    color: 'text-purple-600',
    iconColor: 'text-purple-500',
    gradient: 'from-purple-400 to-purple-600',
    apps: [{
      name: 'TikTok',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fqEytHneLM67yh2EC8qCdG/TikTok.png'
    }, {
      name: 'Instagram',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xyb5gs64F1474oxHMiLRBK/Instagram.png'
    }, {
      name: 'Reddit',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1bwgfvKrYbJ96dGPibLoKP/Reddit.png'
    }, {
      name: 'Snapchat',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qap5aMMHHhbjmXTVnNkEYf/Snapchat.png'
    }]
  }, {
    name: 'Dating Apps',
    icon: HeartIcon,
    color: 'text-pink-500',
    iconColor: 'text-pink-500',
    gradient: 'from-pink-400 to-pink-600',
    apps: [{
      name: 'Tinder',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nuhoZtDe5gBHm49YGRMZj9/Tinder.png'
    }, {
      name: 'Bumble',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/u2o7ZsLyNF3pp8aKnPAzhE/Bumble.png'
    }, {
      name: 'Hinge',
      logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nsY7nsoMEgB7tnvEgWmw9c/Hinge.png'
    }]
  }];
  // Carousel items for feature carousel
  const carouselItems = [{
    id: 1,
    title: 'Reframe',
    description: 'Emigo helps you reframe your message, so you still say what you mean, just without the emotional damage.',
    iconUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sdNfGzwjcXeJDGddhB9ZJ8/image.png',
    color: 'from-[#E87B5C] to-[#FFD700]'
  }, {
    id: 2,
    title: 'Interpret',
    description: "Emigo interprets the tone and intent of a message, so you understand what's really being said before you react.",
    iconUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5ER278KwVKNoQE1xgrmbJ9/image.png',
    color: 'from-[#E87B5C] to-[#FFD700]'
  }, {
    id: 3,
    title: 'Create',
    description: "Emigo turns your feelings and context into a clear, thoughtful message when you're not sure what to say.",
    iconUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iqQTwJrEnvKn9hxoBkYxiy/image.png',
    color: 'from-[#E87B5C] to-[#FFD700]'
  }];
  // Navigation functions for feature carousel
  const nextSlide = () => {
    setCurrentSlide(prev => prev === totalSlides - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? totalSlides - 1 : prev - 1);
  };
  // Refs for each section
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  // Intersection observer to detect when sections come into view
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.25
    };
    const observerCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.dataset.section;
          // Show text first with a delay
          setTimeout(() => {
            setVisibleText(prev => ({
              ...prev,
              [sectionId]: true
            }));
          }, 300);
          // Show images after text with reduced delay (from 2000ms to 1500ms)
          setTimeout(() => {
            setVisibleImages(prev => ({
              ...prev,
              [sectionId]: true
            }));
          }, 1500); // Reduced delay by 0.2 seconds
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    if (section1Ref.current) observer.observe(section1Ref.current);
    if (section2Ref.current) observer.observe(section2Ref.current);
    return () => {
      if (section1Ref.current) observer.unobserve(section1Ref.current);
      if (section2Ref.current) observer.unobserve(section2Ref.current);
    };
  }, []);
  // Helper function to render app icon (either image or placeholder)
  const renderAppIcon = app => {
    if (app.logo) {
      return <img src={app.logo} alt={app.name} className="app-logo" data-id="element-267" />;
    } else {
      return <div className={`w-10 h-10 rounded-full ${app.color} flex items-center justify-center`} data-id="element-268">
          <app.icon className="h-5 w-5 text-white" data-id="element-269" />
        </div>;
    }
  };
  return (
    // Changed to solid white background to match Privacy section start
    <section id="how-it-works" className="pt-8 pb-16 md:pt-12 md:pb-24 bg-white overflow-hidden" data-id="element-270">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-271">
        {/* Section 1: One Tap is all it takes */}
        <div ref={section1Ref} data-section="section1" className="flex flex-col md:flex-row items-center mb-16" data-id="element-272">
          <div className="md:w-1/2 md:pr-12" data-id="element-273">
            <div className={`transition-all duration-700 ease-out transform ${visibleText.section1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`} style={{
              transitionDelay: '300ms'
            }} data-id="element-274">
              <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-275">
                <span className="text-[#E87B5C]" data-id="element-276">One Tap</span>{' '}
                <span className="text-gray-800" data-id="element-277">is all it takes</span>
              </h2>
              <div className="flex flex-col space-y-3 mt-8" data-id="element-278">
                <div className="flex items-center px-4 py-2.5 bg-white border-2 border-[#E87B5C]/30 rounded-full max-w-xs" data-id="element-279">
                  <AppWindowIcon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-280" />
                  <span className="font-semibold text-gray-700" data-id="element-281">
                    Works in All Apps
                  </span>
                </div>
                <div className="flex items-center px-4 py-2.5 bg-white border-2 border-[#E87B5C]/30 rounded-full max-w-xs" data-id="element-282">
                  <ZapIcon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-283" />
                  <span className="font-semibold text-gray-700" data-id="element-284">
                    No App Switching Needed
                  </span>
                </div>
                <div className="flex items-center px-4 py-2.5 bg-white border-2 border-[#E87B5C]/30 rounded-full max-w-xs" data-id="element-285">
                  <Clock3Icon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-286" />
                  <span className="font-semibold text-gray-700" data-id="element-287">
                    Instant Help While You Text
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="md:w-1/2 mt-8 md:mt-0" data-id="element-288">
            <div className={`transition-all duration-1000 ease-out transform ${visibleImages.section1 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`} data-id="element-289">
              {/* Carousel Container - Changed to white background to match section */}
              <div className="relative rounded-2xl border-2 border-[#E87B5C]/30 hover:border-[#E87B5C]/50 transition-all duration-300 w-full overflow-hidden bg-white shadow-md hover:shadow-lg" data-id="element-290">
                {/* Carousel Items */}
                <div className="overflow-hidden rounded-2xl" data-id="element-291">
                  <div className="flex transition-transform duration-500 ease-out" style={{
                    transform: `translateX(-${currentSlide * 100}%)`
                  }} data-id="element-292">
                    {carouselItems.map(item => <div key={item.id} className="w-full flex-shrink-0" data-id="element-293">
                        <div className="bg-white p-8 rounded-2xl" data-id="element-294">
                          {/* Icon and Title in a row */}
                          <div className="flex items-center gap-4 mb-6" data-id="element-295">
                            {item.iconUrl ? <div className="w-12 h-12 rounded-xl shadow-sm flex-shrink-0" style={{
                            backgroundColor: '#E87B5C',
                            backgroundImage: `url(${item.iconUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }} data-id="element-296"></div> : <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{
                            backgroundColor: '#E87B5C'
                          }} data-id="element-297">
                                <item.icon className="h-8 w-8 text-white" data-id="element-298" />
                              </div>}
                            <h3 className="text-xl font-bold text-[#E87B5C]" data-id="element-299">
                              {item.title}
                            </h3>
                          </div>
                          <div className="h-64 bg-gradient-to-br from-[#FFF5EB]/50 to-white rounded-[20px] flex items-center justify-center border border-[#E87B5C]/10 shadow-sm" data-id="element-300">
                            <span className="text-[#E87B5C]" data-id="element-301">
                              Feature Preview
                            </span>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Navigation Buttons */}
                <button onClick={prevSlide} className="absolute top-[60%] left-2 -translate-y-1/2 bg-[#E87B5C] rounded-full p-3 shadow-md z-10 hover:bg-[#D16A4A] hover:scale-110 transition-all duration-300" aria-label="Previous slide" data-id="element-302">
                  <ChevronLeftIcon className="h-5 w-5 text-white" data-id="element-303" />
                </button>
                <button onClick={nextSlide} className="absolute top-[60%] right-2 -translate-y-1/2 bg-[#E87B5C] rounded-full p-3 shadow-md z-10 hover:bg-[#D16A4A] hover:scale-110 transition-all duration-300" aria-label="Next slide" data-id="element-304">
                  <ChevronRightIcon className="h-5 w-5 text-white" data-id="element-305" />
                </button>
                {/* Indicator Dots */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2" data-id="element-306">
                  {Array.from({
                    length: totalSlides
                  }).map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-3 w-3 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-gradient-to-r from-[#E87B5C] to-[#FFD700] scale-110' : 'bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-307"></button>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Single Divider between sections */}
        <SingleDivider data-id="element-308" />

        {/* Section 2: Wherever you chat, Emigo is there to help */}
        <div ref={section2Ref} data-section="section2" className="flex flex-col md:flex-row-reverse items-center mt-16" data-id="element-309">
          <div className="md:w-1/2 md:pl-12" data-id="element-310">
            <div className={`transition-all duration-700 ease-out transform ${visibleText.section2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`} style={{
              transitionDelay: '300ms'
            }} data-id="element-311">
              <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-312">
                <span className="text-[#E87B5C]" data-id="element-313">Wherever</span>{' '}
                <span className="text-gray-800" data-id="element-314">
                  you chat, Emigo is there to help
                </span>
              </h2>
              <div className="flex flex-col space-y-3 mt-8" data-id="element-315">
                <div className="flex items-center px-4 py-2.5 bg-white border-2 border-[#E87B5C]/30 rounded-full max-w-xs" data-id="element-316">
                  <GlobeIcon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-317" />
                  <span className="font-semibold text-gray-700" data-id="element-318">
                    Universal Support
                  </span>
                </div>
                <div className="flex items-center px-4 py-2.5 bg-white border-2 border-[#E87B5C]/30 rounded-full max-w-xs" data-id="element-319">
                  <LayersIcon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-320" />
                  <span className="font-semibold text-gray-700" data-id="element-321">
                    All Major Apps
                  </span>
                </div>
                <div className="flex items-center px-4 py-2.5 bg-white border-2 border-[#E87B5C]/30 rounded-full max-w-xs" data-id="element-322">
                  <PuzzleIcon className="h-5 w-5 mr-3 text-[#E87B5C]" data-id="element-323" />
                  <span className="font-semibold text-gray-700" data-id="element-324">
                    Seamless Integration
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="md:w-1/2 mt-8 md:mt-0" data-id="element-325">
            <div className={`transition-all duration-1000 ease-out transform ${visibleImages.section2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`} data-id="element-326">
              {/* Three stacked carousels - with enhanced container styling */}
              <div className="bg-gradient-to-br from-[#FFF5EB] to-white rounded-[24px] shadow-lg p-6 space-y-6 border border-[#E87B5C]/10" data-id="element-327">
                {/* Messaging Apps Carousel */}
                <div className="space-y-2" data-id="element-328">
                  <div className="flex items-center mb-4" data-id="element-329">
                    <div className="flex items-center" data-id="element-330">
                      <MessageSquareIcon className={`h-6 w-6 mr-3 ${appCategories[0].iconColor}`} fill="currentColor" data-id="element-331" />
                      <h3 className={`text-xl font-bold ${appCategories[0].color}`} data-id="element-332">
                        {appCategories[0].name}
                      </h3>
                    </div>
                  </div>
                  <div className="relative h-20 bg-gradient-to-br from-[#FFF5EB] to-white rounded-lg overflow-hidden" data-id="element-333">
                    <div className="app-ticker" data-id="element-334">
                      <div className="app-track animate-scroll-messaging" data-id="element-335">
                        {/* Duplicate the apps to create seamless loop */}
                        {[...appCategories[0].apps, ...appCategories[0].apps].map((app, index) => <div key={`messaging-${index}`} className="app-item" data-id="element-336">
                            <div className="flex items-center justify-center" data-id="element-337">
                              {renderAppIcon(app)}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Social Media Carousel */}
                <div className="space-y-2" data-id="element-338">
                  <div className="flex items-center mb-4" data-id="element-339">
                    <div className="flex items-center" data-id="element-340">
                      <UsersIcon className={`h-6 w-6 mr-3 ${appCategories[1].iconColor}`} fill="currentColor" data-id="element-341" />
                      <h3 className={`text-xl font-bold ${appCategories[1].color}`} data-id="element-342">
                        {appCategories[1].name}
                      </h3>
                    </div>
                  </div>
                  <div className="relative h-20 bg-gradient-to-br from-[#FFF5EB] to-white rounded-lg overflow-hidden" data-id="element-343">
                    <div className="app-ticker" data-id="element-344">
                      <div className="app-track animate-scroll-social" data-id="element-345">
                        {/* Duplicate the apps to create seamless loop */}
                        {[...appCategories[1].apps, ...appCategories[1].apps].map((app, index) => <div key={`social-${index}`} className="app-item" data-id="element-346">
                            <div className="flex items-center justify-center" data-id="element-347">
                              {renderAppIcon(app)}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Dating Apps Carousel */}
                <div className="space-y-2" data-id="element-348">
                  <div className="flex items-center mb-4" data-id="element-349">
                    <div className="flex items-center" data-id="element-350">
                      <HeartIcon className={`h-6 w-6 mr-3 ${appCategories[2].iconColor}`} fill="currentColor" data-id="element-351" />
                      <h3 className={`text-xl font-bold ${appCategories[2].color}`} data-id="element-352">
                        {appCategories[2].name}
                      </h3>
                    </div>
                  </div>
                  <div className="relative h-20 bg-gradient-to-br from-[#FFF5EB] to-white rounded-lg overflow-hidden" data-id="element-353">
                    <div className="app-ticker" data-id="element-354">
                      <div className="app-track animate-scroll-dating" data-id="element-355">
                        {/* Duplicate the apps to create seamless loop */}
                        {[...appCategories[2].apps, ...appCategories[2].apps].map((app, index) => <div key={`dating-${index}`} className="app-item" data-id="element-356">
                            <div className="flex items-center justify-center" data-id="element-357">
                              {renderAppIcon(app)}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default HowItWorksSection;